'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden gradient-hero grid-pattern flex-col">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 blob-primary opacity-50 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 blob-accent opacity-40 pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10 p-8">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={36} />
            <span className="font-bold text-xl gradient-primary-text">GameForge</span>
          </Link>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-12 xl:px-16">
          <h1 className="text-hero-xl text-foreground mb-6">
            Cria jogos com{' '}
            <span className="gradient-primary-text">Inteligência Artificial</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-10 max-w-md leading-relaxed">
            Escreve um prompt. A IA cria o teu jogo em segundos. Testa, edita e publica para a comunidade.
          </p>

          {/* Features */}
          <div className="space-y-4">
            {[
              { icon: 'BoltIcon', text: '75 créditos gratuitos todos os dias' },
              { icon: 'SparklesIcon', text: 'Geração de jogos com IA avançada' },
              { icon: 'WrenchScrewdriverIcon', text: 'Editor integrado com chat de IA' },
              { icon: 'GlobeAltIcon', text: 'Partilha com a comunidade global' },
            ].map((feat) => (
              <div key={`feat-${feat.icon}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name={feat.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
                </div>
                <span className="text-sm text-foreground">{feat.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div className="relative z-10 p-8 border-t border-border/30">
          <div className="flex gap-8">
            {[
              { value: '12.400+', label: 'Jogos criados' },
              { value: '3.200+', label: 'Criadores' },
              { value: '99.9%', label: 'Uptime' },
            ].map((stat) => (
              <div key={`auth-stat-${stat.label}`}>
                <p className="text-xl font-bold text-foreground tabular-nums">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-12 xl:px-16 bg-background overflow-y-auto py-8">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-xl gradient-primary-text">GameForge</span>
        </div>

        <div className="w-full max-w-md mx-auto">
          {/* Tab switcher */}
          <div className="flex rounded-xl bg-muted/50 p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Criar conta
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignup={() => setMode('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}